/**
 * F 229 — humanising the history log for reading.
 *
 * Everything here is PURE and exported so it can be tested without a DOM.
 * The component renders; this decides what the words are.
 *
 * WHY THIS FILE EXISTS AT ALL. F 228's panel rendered the raw record:
 * "2026-10-11 12:09 deletedAt 2026-10-11T12:08:59.516Z deletedAt". Every
 * token in that line is machine vocabulary. A change log is read by people,
 * so the panel has to say "Restored" rather than "deletedAt", and
 * "2 hours ago" rather than an ISO string. The data is unchanged; only
 * the words on top of it change.
 *
 * THE SUMMARY IS DERIVED FROM THE DELTA, NOT STORED. `deletedAt` going
 * from a value to null is a restore; going from null to a value is a
 * delete; anything else is a change to that field. Deriving it means a
 * record written by a future writer with a field this file has never seen
 * still summarises as "Changed <field>" rather than rendering as garbage.
 */

import type { HistoryRecord, ChangedFields } from '../../../core/workspace/entityHistory';

/** A field's human name. The raw key is never shown on its own. */
export const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  name: 'Name',
  body: 'Body',
  content: 'Content',
  parentId: 'Parent',
  order: 'Position',
  edge: 'Edge',
  status: 'Status',
  kind: 'Kind',
  deletedAt: 'Deleted at',
  archived: 'Archived',
  assignee: 'Assignee',
  dueDate: 'Due date',
  priority: 'Priority',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

export type HistoryVerb = 'created' | 'deleted' | 'restored' | 'changed' | 'moved';

export interface HistorySummary {
  verb: HistoryVerb;
  /** "Restored", "Deleted", "Changed Title". */
  text: string;
  /** Tabler icon name, so the component maps it rather than this file. */
  icon: 'plus' | 'trash' | 'restore' | 'pencil' | 'move';
}

/**
 * One human sentence for a record.
 *
 * The order of the checks is the whole design: a soft-delete and a
 * restore are both expressed as a `deletedAt` delta, and they are
 * opposites. `after === null` is a restore because the entity came BACK.
 */
export function summariseRecord(record: HistoryRecord): HistorySummary {
  const d: ChangedFields = record.changedFields ?? {};
  const keys = Object.keys(d);

  if (keys.length === 0) {
    return { verb: 'changed', text: 'No fields changed', icon: 'pencil' };
  }

  // Created: every tracked field went absent -> present.
  if (keys.length > 1 && keys.every((k) => d[k].before === null && d[k].after !== null)) {
    return { verb: 'created', text: 'Created', icon: 'plus' };
  }
  // A single field that went from nothing to a value is a creation of
  // that field; the panel says "Created" only when the whole entity is new.
  if (keys.length === 1 && keys[0] !== 'deletedAt' && d[keys[0]].before === null && d[keys[0]].after !== null) {
    return { verb: 'created', text: `Created ${fieldLabel(keys[0])}`, icon: 'plus' };
  }

  if ('deletedAt' in d) {
    if (d.deletedAt.after === null || d.deletedAt.after === undefined) {
      return { verb: 'restored', text: 'Restored', icon: 'restore' };
    }
    return { verb: 'deleted', text: 'Deleted', icon: 'trash' };
  }

  if ('parentId' in d || 'order' in d) {
    const which = 'parentId' in d ? 'Parent' : 'Position';
    return { verb: 'moved', text: `Changed ${which}`, icon: 'move' };
  }

  return { verb: 'changed', text: `Changed ${fieldLabel(keys[0])}`, icon: 'pencil' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Relative time
// ─────────────────────────────────────────────────────────────────────────────

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "just now" / "12 minutes ago" / "2 hours ago" / "3 days ago" / a date
 * past a month.
 *
 * `now` is an argument so the function is deterministic under test — a
 * relative formatter that reads the clock is a formatter that can only
 * be tested by waiting.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = now - t;
  if (diff < 0) return 'just now';
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 30 * DAY) {
    const d = Math.floor(diff / DAY);
    return `${d} day${d === 1 ? '' : 's'} ago`;
  }
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** The exact UTC stamp, for the `title` attribute. */
export function exactTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')}`;
}

/** Value as a short display string, for before/after. */
export function formatValue(value: unknown, max = 120): string {
  if (value === null || value === undefined) return '—';
  let s: string;
  if (typeof value === 'string') s = value;
  else if (typeof value === 'object') {
    try { s = JSON.stringify(value); } catch { s = '[object]'; }
  } else s = String(value);
  if (s === '') return '(empty)';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** The short inline field list shown before a row is expanded. */
export function inlineFields(record: HistoryRecord): string {
  return Object.keys(record.changedFields ?? {}).map(fieldLabel).join(', ');
}
